/**
 * Semantic colour tokens.
 *
 * This is the layer the rest of the app talks to. A component asks for
 * `colors.text.secondary`, never `palette.teal[700]` — so when the identity
 * shifts again, the meaning of "secondary text" stays put and only its value
 * moves.
 *
 * Most of these are a direct translation of the `--` aliases in the design
 * system's `tokens/colors.css`; the brand's own name is recorded in a comment
 * wherever the app's name differs, so the two can be reconciled later. Three
 * kinds of entry are *not* direct translations, and each says so:
 *
 * 1. Tokens the design system has no counterpart for, because a deck has no
 *    chat bubbles or disabled inputs.
 * 2. Tokens where the design system's value fails WCAG AA in the role it is
 *    named for. `--text-accent` is peche at full strength: 2.27:1 on white.
 *    Those become a `*Strong` variant, and the original stays available as a
 *    fill, which is what it is good at.
 * 3. `surface.page`, which follows the design system to white; nude is a
 *    section surface (`--surface-calme`), not the ground under everything.
 *
 * Contrast: every foreground/background pairing named here has been checked
 * against WCAG AA (4.5:1 for body text, 3:1 for large text and non-text
 * boundaries). Where a pairing sits below AA it is because the token is
 * deliberately recessive — `text.disabled` and `text.placeholder` — and those
 * are never the sole carrier of meaning.
 */

import { gold, palette, symptomSeverity, withAlpha } from './palette';

export const colors = {
  /* ---------------------------------------------------------------------- */
  /*  Brand                                                                   */
  /* ---------------------------------------------------------------------- */
  brand: {
    /** turquoise #84D1BF. `--action-primary-bg`. Primary actions and anything that must read as "us". */
    primary: palette.mint[400],
    /** `--action-primary-bg-hover`. */
    primaryHover: palette.mint[600],
    primaryActive: palette.mint[600],
    /** A wash of the primary, for selected rows and quiet brand surfaces. */
    primarySubtle: palette.mint[50],
    /** `--action-primary-fg`. Dark, not white — white on turquoise is 1.8:1. 7.26:1. */
    onPrimary: palette.teal[700],
    /**
     * The primary at a weight that can carry text or an icon, the way
     * `accentStrong` / `highlightStrong` do for paon and peche. Turquoise
     * itself is 1.77:1 on white and cannot be read; this is `derived.mintText`
     * — 4.58:1 on nude (the worst light surface), 5.65:1 on white.
     */
    primaryStrong: palette.derived.mintText,

    /** paon #033744. `--action-secondary-bg`. The depth accent, as a *fill*. */
    accent: palette.teal[700],
    /** `--action-secondary-bg-hover`. */
    accentHover: palette.teal[500],
    accentSubtle: palette.teal[100],
    /** `--action-secondary-fg`. White on paon is 12.85:1. */
    onAccent: palette.white,
    /**
     * The accent at a weight that can carry text or an icon on a light surface.
     *
     * Paon (#033744) is 12.85:1 on white and clears AA on every surface in the
     * app — no derived mix required.
     */
    accentStrong: palette.teal[700],

    /** peche #FF8B7C. `--action-accent-bg`. The warm accent, for attention without alarm. */
    highlight: palette.coral[400],
    /** `--action-accent-bg-hover`. */
    highlightHover: palette.coral[600],
    highlightSubtle: palette.coral[50],
    /** `--action-accent-fg`. White on peche is 2.3:1, so: dark. 5.65:1. */
    onHighlight: palette.teal[700],
    /** The warm accent at a readable weight. Replaces `--text-accent` (2.27:1). */
    highlightStrong: palette.derived.coralText,

    /**
     * `--degrade-diagonal`, the brand's signature gradient.
     *
     * @deprecated Use `colors.gradient.brand.value`, and read the warning
     * there before putting text on it.
     */
    accentGradient: `linear-gradient(135deg, ${palette.teal[700]} 0%, ${palette.mint[400]} 38%, ${palette.sand[200]} 66%, ${palette.coral[400]} 100%)`,
  },

  /* ---------------------------------------------------------------------- */
  /*  Gradients                                                               */
  /* ---------------------------------------------------------------------- */
  /**
   * One gradient per brand colour, plus the signature.
   *
   * Each carries the foreground that goes on it, and each has been checked at
   * *both* ends — which is the whole difficulty with a gradient. A ratio taken
   * at the midpoint is meaningless: what matters is the worst point the text
   * actually crosses. The four family gradients stay inside one hue and move a
   * couple of steps, so they read as a wash rather than as a colour change, and
   * their `on` value clears AA along their entire length.
   *
   * They are named for the *role* the colour plays, like everything else here,
   * so `primary` follows `brand.primary` if the identity reassigns it.
   */
  gradient: {
    /** Turquoise, the primary. Worst end 7.26:1 with `on`. */
    primary: {
      value: `linear-gradient(135deg, ${palette.mint[200]} 0%, ${palette.mint[400]} 100%)`,
      on: palette.teal[700],
    },
    /** Deep teal. The one dark gradient, so it takes white. Worst end 8.63:1. */
    accent: {
      value: `linear-gradient(135deg, ${palette.teal[500]} 0%, ${palette.teal[900]} 100%)`,
      on: palette.white,
    },
    /** Peche, for warmth and attention. Worst end 5.65:1. */
    highlight: {
      value: `linear-gradient(135deg, ${palette.coral[200]} 0%, ${palette.coral[400]} 100%)`,
      on: palette.teal[700],
    },
    /** Nude, the calm ground. Worst end 8.78:1. */
    calm: {
      value: `linear-gradient(135deg, ${palette.sand[100]} 0%, ${palette.sand[300]} 100%)`,
      on: palette.teal[700],
    },

    /**
     * The signature, `--degrade-diagonal`: teal through turquoise and nude to
     * peche.
     *
     * **No single foreground is readable across it.** Deep teal clears AA on
     * the turquoise, nude and peche stops (7.26, 10.42, 5.65) and sits at
     * 1.00:1 on the teal stop; white is the exact inverse — 12.85 on teal, and
     * 1.77 / 1.23 / 2.27 on the rest. There is no colour that works at both
     * ends, because the gradient spans nearly the full lightness range on
     * purpose.
     *
     * So it is decorative. Use it behind nothing, or put text on a plate: the
     * `scrim` below is `--scrim-bas` from the design system and gives white a
     * readable ground over any part of it.
     */
    brand: {
      value: `linear-gradient(135deg, ${palette.teal[700]} 0%, ${palette.mint[400]} 38%, ${palette.sand[200]} 66%, ${palette.coral[400]} 100%)`,
      /** `--scrim-bas`. Lay this over the gradient, then white text on top. */
      scrim: `linear-gradient(to top, ${withAlpha(palette.teal[700], 0.72)} 0%, ${withAlpha(palette.teal[700], 0)} 62%)`,
      on: palette.white,
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  Surfaces                                                                */
  /* ---------------------------------------------------------------------- */
  surface: {
    /**
     * The page behind everything: `--peche-050`, the lightest peach.
     *
     * A deliberate step away from the design system, which sets
     * `--surface-page` to white. White is right for a deck, where a slide is a
     * bounded object on a dark stage; a phone has no stage, so an all-white app
     * reads as clinical rather than serene, and cards stop reading as cards
     * because there is nothing for them to sit on.
     *
     * Peach rather than nude because nude at full strength was the old page and
     * spent too much of the contrast budget — text on it measures 3.79:1 at the
     * tier the brand specifies. This is light enough that every text token
     * clears AA on it with room to spare (4.93:1 at worst), while cards in
     * `surface.base` white still lift off it without a shadow.
     */
    page: palette.sand[50],
    /** `--surface-card`. Cards, sheets, list rows. */
    base: palette.white,
    /** Raised above `base`. Flat identity, so this is contrast, not shadow. */
    raised: palette.white,
    /** `--surface-calme`. Calm sections, illustration grounds, input wells. */
    sunken: palette.sand[200],
    /** `--surface-card-calme`. The lightest warm tint, for quiet cards. */
    calm: palette.sand[100],
    /** A cool neutral fill, for when a warm nude tint would read as a colour. */
    muted: palette.derived.neutral50,
    /** `--surface-inverse`. */
    brand: palette.teal[700],
    brandSubtle: palette.teal[100],
    /** `--surface-medical`. */
    accentSubtle: palette.mint[50],
    /** `--surface-highlight`. */
    highlightSubtle: palette.coral[50],
    inverse: palette.teal[700],
    /** `--state-disabled-bg`. */
    disabled: palette.sand[200],
    /** `--scrim-plein`. */
    scrim: withAlpha(palette.teal[700], 0.42),
  },

  /* ---------------------------------------------------------------------- */
  /*  Text                                                                    */
  /* ---------------------------------------------------------------------- */
  text: {
    /** `--text-primary`. 12.85:1 on white. */
    primary: palette.teal[700],
    /**
     * Supporting copy. 6.36:1 on white, 5.15:1 on nude.
     *
     * One step darker than the design system's `--text-secondary` (#4C7B87).
     * That value is 4.68:1 on white — it passes, but it leaves nothing lighter
     * to spend on a third tier, and it drops to 3.79:1 on a nude panel. Moving
     * secondary down a step buys a metadata tier that still clears AA.
     */
    secondary: palette.derived.tealMid,
    /**
     * Timestamps, counts, hints. 5.61:1 on white, 4.55:1 on nude.
     *
     * The design system's `--text-secondary` verbatim (#4C7B87) is 4.68:1 on
     * white and fails everywhere else the app uses it — 4.25:1 on the mint
     * panel, 3.79:1 on nude. This is that colour darkened until the worst
     * surface clears AA.
     */
    tertiary: palette.derived.tealText,
    /** Deliberately recessive. Never the only signal that something is off. */
    placeholder: palette.derived.disabledText,
    /** `--state-disabled-fg`. */
    disabled: palette.derived.disabledText,
    /** `--text-inverse` / `--text-on-paon`. */
    inverse: palette.white,
    brand: palette.teal[700],
    /** `--text-link`. */
    link: palette.teal[700],
    /** `--text-link-hover` is peche-600 at 2.87:1; this is the readable form. */
    linkHover: palette.derived.coralText,
    error: palette.derived.coralText,
    success: palette.derived.mintText,
    warning: palette.teal[500],
  },

  /* ---------------------------------------------------------------------- */
  /*  Borders                                                                 */
  /* ---------------------------------------------------------------------- */
  border: {
    /** Hairlines and dividers — barely there, which is the point. */
    subtle: palette.derived.neutral100,
    /** `--border-subtle`. */
    default: palette.sand[300],
    /** `--border-strong`, softened one step so an input outline is not a black box. */
    strong: palette.teal[300],
    /** `--border-strong`. */
    brand: palette.teal[700],
    /** `--border-medical`. */
    accent: palette.mint[400],
    /** `--focus-ring`. */
    focus: palette.coral[400],
    error: palette.coral[600],
  },

  /* ---------------------------------------------------------------------- */
  /*  Feedback                                                                */
  /* ---------------------------------------------------------------------- */
  /**
   * Each status carries a full set so a component never has to improvise:
   * `fg` on `bg` clears AA, `solid` is the filled variant, `onSolid` is what
   * goes on top of it.
   *
   * The design system has three states (`--state-success`, `--state-attention`,
   * `--state-info`) and each is a single value, which is enough for a deck. An
   * app needs the surrounding set, and it needs a fourth state: with only these
   * hues, warning and error both want to be peche, and then "waiting a while"
   * and "this is wrong" render identically — which is the one distinction a
   * status chip exists to make. Warning therefore sits in the nude family, the
   * calmer half of that split, keeping peche for error.
   */
  feedback: {
    success: {
      fg: palette.derived.mintText,
      bg: palette.mint[50],
      border: palette.mint[200],
      solid: palette.mint[600],
      onSolid: palette.teal[700],
    },
    info: {
      fg: palette.teal[500],
      bg: palette.teal[100],
      border: palette.teal[300],
      solid: palette.teal[500],
      onSolid: palette.white,
    },
    warning: {
      fg: palette.teal[500],
      bg: palette.sand[200],
      border: palette.sand[300],
      solid: palette.coral[400],
      onSolid: palette.teal[700],
    },
    error: {
      fg: palette.derived.coralText,
      bg: palette.coral[50],
      border: palette.coral[400],
      solid: palette.coral[600],
      onSolid: palette.teal[700],
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  Chat                                                                    */
  /* ---------------------------------------------------------------------- */
  /**
   * Message bubbles. The patient's own messages carry the primary (turquoise)
   * gradient; the caregiver's sit on a cool neutral, so the conversation reads
   * as "mine / theirs" at a glance without either side shouting.
   */
  chat: {
    me: {
      /** `gradient.primary.value` — a turquoise wash, not a flat mint fill. */
      background: palette.mint[200],
      /** `gradient.primary.on`. Dark, not white — white on turquoise is 1.8:1. 7.26:1 at the worst end. */
      text: palette.teal[700],
      title: palette.derived.mintText,
    },
    them: {
      background: palette.derived.neutral100,
      text: palette.teal[700],
      title: palette.derived.tealMid,
    },
  },

  /* ---------------------------------------------------------------------- */
  /*  Data visualisation                                                      */
  /* ---------------------------------------------------------------------- */
  /**
   * The five-step qualitative scale used by the symptom and cycle charts and
   * the general-state / pain survey questions.
   *
   * `palette.symptomSeverity` — see the note there. This used to run
   * turquoise through nude to peche, staying inside the brand family and
   * monotonic in lightness (so it survived greyscale and red/green colour
   * blindness); it now runs an explicit green-to-red traffic light instead,
   * on request, which reads faster as a severity scale than the brand ramp
   * did but gives up that colour-blind safety net.
   */
  dataViz: {
    veryGood: symptomSeverity.veryGood,
    good: symptomSeverity.good,
    average: symptomSeverity.average,
    bad: symptomSeverity.bad,
    veryBad: symptomSeverity.veryBad,
    /** Ordered form, for charts that map an index onto the scale. */
    scale: [
      symptomSeverity.veryGood,
      symptomSeverity.good,
      symptomSeverity.average,
      symptomSeverity.bad,
      symptomSeverity.veryBad,
    ],
    /** Series colours for multi-line charts. */
    series: [
      palette.teal[700],
      palette.mint[600],
      palette.coral[400],
      palette.teal[300],
      palette.sand[300],
      palette.derived.mintText,
    ],
    grid: palette.derived.neutral100,
    axis: palette.teal[300],
  },

  /* ---------------------------------------------------------------------- */
  /*  Patient access tiers                                                    */
  /* ---------------------------------------------------------------------- */
  /** Icon colours for the badge that marks how a patient accesses the app. */
  accessTier: {
    /** `gold` in `palette.ts` — see the note there. */
    licenseCode: gold,
    /** Closest in-palette step to the old green (`#7FBAA9`). */
    appSubscription: palette.mint[600],
    /**
     * Not `text.tertiary`, which is a saturated blue: `disabledText` is the
     * palette's closest tone that actually reads as a neutral grey.
     */
    free: palette.derived.disabledText,
  },
} as const;

export type Colors = typeof colors;
